import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import Topbar from '../../components/Topbar/Topbar';
import RightSidebar from '../../components/RightSidebar/RightSidebar';
import './Groups.css';

import API_URL from '../../config';
import AutocompleteInput from '../../components/common/AutocompleteInput';
import { CITY_LIST, getDistricts } from '../../data/turkeyGeo';

const PostCreator = lazy(() => import('../../components/PostCreator/PostCreator'));

/* ─── Constants ─── */
const CATEGORIES = [
  { key: 'region', label: 'Bolge' },
  { key: 'school', label: 'Okul/Mezuniyet' },
  { key: 'discipline', label: 'Disiplin' },
  { key: 'interest', label: 'Ilgi Alani' },
  { key: 'custom', label: 'Ozel' },
];

const VISIBILITY_OPTIONS = [
  { key: 'open', label: 'Acik' },
  { key: 'closed', label: 'Kapali' },
  { key: 'private', label: 'Ozel' },
];

const ORG_TYPES = [
  { key: 'engineering_bureau', label: 'Muhendislik Burosu' },
  { key: 'construction_company', label: 'Insaat Sirketi' },
  { key: 'inspection_firm', label: 'Denetim Firmasi' },
  { key: 'municipality', label: 'Belediye' },
  { key: 'fire_department', label: 'Itfaiye' },
  { key: 'osb', label: 'OSB' },
  { key: 'university', label: 'Universite' },
  { key: 'chamber', label: 'Oda' },
  { key: 'supplier_company', label: 'Tedarikci Sirket' },
  { key: 'energy_company', label: 'Enerji Sirketi' },
  { key: 'telecom_company', label: 'Telekom Sirketi' },
  { key: 'other', label: 'Diger' },
];

const TEAM_ROLES = [
  { key: 'malSahibi', label: 'Mal Sahibi / Isveren' },
  { key: 'muteahhit', label: 'Muteahhit' },
  { key: 'mimariMuellif', label: 'Mimari Muellif' },
  { key: 'elektrikMuellif', label: 'Elektrik Muellif' },
  { key: 'mekanikMuellif', label: 'Mekanik Muellif' },
  { key: 'kontrolMuhendis', label: 'Kontrol Muhendisi' },
  { key: 'yapiDenetim', label: 'Yapi Denetim' },
  { key: 'elektrikDagitim', label: 'Elektrik Dagitim' },
  { key: 'telekom', label: 'Telekom' },
  { key: 'itfaiye', label: 'Itfaiye' },
];

const teamRoleLabel = (key) => TEAM_ROLES.find(r => r.key === key)?.label || key;
const categoryLabel = (key) => CATEGORIES.find(c => c.key === key)?.label || key;
const visibilityLabel = (key) => VISIBILITY_OPTIONS.find(v => v.key === key)?.label || key;
const orgTypeLabel = (key) => ORG_TYPES.find(o => o.key === key)?.label || key;

/* ─── Icons ─── */
const IconPlus = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
);
const IconUsers = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
);
const IconSearch = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
);
const IconLock = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
);
const IconGlobe = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
);
const IconEyeOff = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
);
const IconX = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
);
const IconBack = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
);
const IconSend = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
);
const IconBuilding = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><line x1="9" y1="6" x2="9" y2="6.01"/><line x1="15" y1="6" x2="15" y2="6.01"/><line x1="9" y1="10" x2="9" y2="10.01"/><line x1="15" y1="10" x2="15" y2="10.01"/><line x1="9" y1="14" x2="9" y2="14.01"/><line x1="15" y1="14" x2="15" y2="14.01"/><line x1="9" y1="18" x2="15" y2="18"/></svg>
);
const IconLogout = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
);

const visibilityIcon = (v) => {
  if (v === 'open') return <IconGlobe />;
  if (v === 'closed') return <IconLock />;
  return <IconEyeOff />;
};

/* ─── Relative time helper ─── */
function relativeTime(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diffSec = Math.floor((now - d) / 1000);
  if (diffSec < 60) return 'Az once';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} dk once`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} saat once`;
  const days = Math.floor(diffHr / 24);
  if (days < 7) return `${days}g once`;
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
}


/* ═══════════════════════════════════════════════
   ORG SETTINGS TAB (editable)
   ═══════════════════════════════════════════════ */
const OrgSettingsTab = ({ orgDetail, token, onSaved }) => {
  const [name, setName] = useState(orgDetail.name || '');
  const [type, setType] = useState(orgDetail.type || 'other');
  const [description, setDescription] = useState(orgDetail.description || '');
  const [city, setCity] = useState(orgDetail.city || '');
  const [district, setDistrict] = useState(orgDetail.district || '');
  const [website, setWebsite] = useState(orgDetail.website || '');
  const [email, setEmail] = useState(orgDetail.contactInfo?.email || '');
  const [phone, setPhone] = useState(orgDetail.contactInfo?.phone || '');
  const [whatsapp, setWhatsapp] = useState(orgDetail.contactInfo?.whatsapp || '');
  const [address, setAddress] = useState(orgDetail.contactInfo?.address || '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      const res = await fetch(`${API_URL}/api/organizations/${orgDetail.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, type, description, city, district, website, contactInfo: { email, phone, whatsapp, address } })
      });
      if (res.ok) {
        const data = await res.json();
        onSaved(data.organization || {});
        setMsg('Kaydedildi.');
      } else {
        const err = await res.json();
        setMsg(err.message || 'Hata olustu.');
      }
    } catch { setMsg('Baglanti hatasi.'); }
    setSaving(false);
    setTimeout(() => setMsg(''), 3000);
  };

  return (
    <div className="groups-section groups-settings-edit">
      <label>Organizasyon Adi
        <input type="text" value={name} onChange={e => setName(e.target.value)} />
      </label>
      <label>Tur
        <select value={type} onChange={e => setType(e.target.value)}>
          {ORG_TYPES.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </label>
      <label>Aciklama
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} />
      </label>
      <div className="groups-modal-row">
        <label>Il
          <AutocompleteInput value={city} onChange={v => { setCity(v); setDistrict(''); }} options={CITY_LIST} placeholder="Il secin..." />
        </label>
        <label>Ilce
          <AutocompleteInput value={district} onChange={v => setDistrict(v)} options={city ? getDistricts(city) : []} placeholder="Ilce secin..." />
        </label>
      </div>
      <label>Web Sitesi
        <input type="text" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://..." />
      </label>

      <div className="groups-modal-section-title">Iletisim Bilgileri</div>
      <div className="groups-modal-row">
        <label>E-posta
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="info@firma.com" />
        </label>
        <label>Telefon
          <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="0312 000 00 00" />
        </label>
      </div>
      <div className="groups-modal-row">
        <label>WhatsApp
          <input type="tel" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} placeholder="+90 5XX..." />
        </label>
        <label>Adres
          <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="Acik adres" />
        </label>
      </div>

      <div className="groups-settings-row" style={{marginTop:'.5rem'}}>
        <span className="groups-settings-label">Olusturulma</span>
        <span className="groups-settings-value">{orgDetail.createdAt ? new Date(orgDetail.createdAt).toLocaleDateString('tr-TR') : '-'}</span>
      </div>

      <div className="groups-settings-actions">
        <button className="groups-modal-submit" onClick={handleSave} disabled={saving || !name.trim()}>
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
        {msg && <span className="groups-settings-msg">{msg}</span>}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   ORG INBOX (project review invitations)
   ═══════════════════════════════════════════════ */
const INVITATION_STATUS_LABELS = {
  pending: 'Bekliyor', viewed: 'Goruldu', in_review: 'Inceleniyor',
  approved: 'Onaylandi', rejected: 'Reddedildi', revision_requested: 'Revizyon Istendi'
};
const INVITATION_STATUS_COLORS = {
  pending: '#eab308', viewed: '#8b5cf6', in_review: '#3b82f6',
  approved: '#22c55e', rejected: '#ef4444', revision_requested: '#f97316'
};

const OrgInbox = ({ orgId, token }) => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/project-invitations/inbox`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setInvitations((data.invitations || []).filter(inv => inv.targetOrganizationId === orgId));
        }
      } catch (e) { console.error('Inbox error:', e); }
      setLoading(false);
    })();
  }, [orgId, token]);

  const respond = async (invId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/project-invitations/${invId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (res.ok) {
        const data = await res.json();
        setInvitations(prev => prev.map(i => i.id === invId ? { ...i, ...data.invitation } : i));
      }
    } catch (e) { console.error('Respond error:', e); }
  };

  if (loading) return <div className="groups-empty-text">Yukleniyor...</div>;
  if (invitations.length === 0) return <div className="groups-empty-text">Gelen davet yok.</div>;

  return (
    <div className="groups-section org-inbox">
      {invitations.map(inv => (
        <div key={inv.id} className="org-inbox-card">
          <div className="org-inbox-card-header">
            <div className="org-inbox-project">{inv.project?.name || 'Proje'}</div>
            <span className="org-inbox-status" style={{color: INVITATION_STATUS_COLORS[inv.status] || '#888'}}>
              {INVITATION_STATUS_LABELS[inv.status] || inv.status}
            </span>
          </div>
          <div className="org-inbox-meta">
            {inv.sender && <span>Gonderen: {inv.sender.firstName} {inv.sender.lastName}</span>}
            <span>{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString('tr-TR') : ''}</span>
          </div>
          {inv.message && <div className="org-inbox-message">{inv.message}</div>}
          {(inv.status === 'pending' || inv.status === 'viewed' || inv.status === 'in_review') && (
            <div className="org-inbox-actions">
              {inv.status === 'pending' && <button className="org-inbox-btn review" onClick={() => respond(inv.id, 'in_review')}>Incelemeye Al</button>}
              <button className="org-inbox-btn approve" onClick={() => respond(inv.id, 'approved')}>Onayla</button>
              <button className="org-inbox-btn reject" onClick={() => respond(inv.id, 'rejected')}>Reddet</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   GROUPS PAGE
   ═══════════════════════════════════════════════ */
const Groups = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [theme] = useState(() => localStorage.getItem('projabze_theme') || 'dark');
  const token = localStorage.getItem('projabze_token');

  /* ─── Tab state ─── */
  const [activeTab, setActiveTab] = useState('teams'); // 'teams' | 'my' | 'discover' | 'orgs'

  /* ─── Teams ─── */
  const [myTeams, setMyTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [ctName, setCtName] = useState('');
  const [ctDesc, setCtDesc] = useState('');
  const [ctOwnerRole, setCtOwnerRole] = useState('');
  const [ctMembers, setCtMembers] = useState([]); // [{ role, userId, firstName, lastName, search, results, loading }]
  const [creatingTeam, setCreatingTeam] = useState(false);
  const ctSearchTimers = useRef({});
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamDetail, setTeamDetail] = useState(null);
  const [loadingTeamDetail, setLoadingTeamDetail] = useState(false);
  const [addMemberRole, setAddMemberRole] = useState('');
  const [addMemberSearch, setAddMemberSearch] = useState('');
  const [addMemberResults, setAddMemberResults] = useState([]);
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const addMemberTimer = useRef(null);

  /* ─── My groups ─── */
  const [myGroups, setMyGroups] = useState([]);
  const [loadingMyGroups, setLoadingMyGroups] = useState(false);

  /* ─── Discover ─── */
  const [discoverGroups, setDiscoverGroups] = useState([]);
  const [discoverOrgs, setDiscoverOrgs] = useState([]);
  const [discoverUsers, setDiscoverUsers] = useState([]);
  const [discoverCategory, setDiscoverCategory] = useState('');
  const [discoverSearch, setDiscoverSearch] = useState('');
  const discoverSearchRef = useRef('');
  const [loadingDiscover, setLoadingDiscover] = useState(false);

  /* ─── Organizations ─── */
  const [myOrgs, setMyOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [orgSearch, setOrgSearch] = useState('');

  /* ─── Group detail ─── */
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupDetail, setGroupDetail] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupFeed, setGroupFeed] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [newGroupPost, setNewGroupPost] = useState('');
  const [postingToGroup, setPostingToGroup] = useState(false);
  const [detailTab, setDetailTab] = useState('home');
  const [orgFeed, setOrgFeed] = useState([]);
  const [orgDetailTab, setOrgDetailTab] = useState('home');
  const [newOrgPost, setNewOrgPost] = useState('');
  const [postingToOrg, setPostingToOrg] = useState(false);

  /* ─── Org detail ─── */
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDetail, setOrgDetail] = useState(null);
  const [loadingOrgDetail, setLoadingOrgDetail] = useState(false);

  /* ─── Create group modal ─── */
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [cgName, setCgName] = useState('');
  const [cgDesc, setCgDesc] = useState('');
  const [cgCategory, setCgCategory] = useState('custom');
  const [cgVisibility, setCgVisibility] = useState('open');
  const [cgCity, setCgCity] = useState('');
  const [cgTags, setCgTags] = useState('');
  const [cgHasFeed, setCgHasFeed] = useState(true);
  const [creatingGroup, setCreatingGroup] = useState(false);

  /* ─── Create org modal ─── */
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [coName, setCoName] = useState('');
  const [coType, setCoType] = useState('engineering_bureau');
  const [coDesc, setCoDesc] = useState('');
  const [coCity, setCoCity] = useState('');
  const [coDistrict, setCoDistrict] = useState('');
  const [coWebsite, setCoWebsite] = useState('');
  const [coEmail, setCoEmail] = useState('');
  const [coPhone, setCoPhone] = useState('');
  const [coWhatsapp, setCoWhatsapp] = useState('');
  const [coAddress, setCoAddress] = useState('');
  const [creatingOrg, setCreatingOrg] = useState(false);

  /* ─── Joining state ─── */
  const [joiningId, setJoiningId] = useState(null);

  /* ─── Auth init ─── */
  useEffect(() => {
    const stored = localStorage.getItem('projabze_user');
    if (stored) {
      setUser(JSON.parse(stored));
    } else {
      navigate('/');
    }
  }, [navigate]);

  /* ─── URL query param: ?team=ID ─── */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const teamId = params.get('team');
    if (teamId && user) {
      setActiveTab('teams');
      openTeamDetail(teamId);
      // URL'den param'ı temizle
      window.history.replaceState({}, '', window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  /* ═══════════════════════════════════════════════
     DATA FETCHING
     ═══════════════════════════════════════════════ */

  const fetchMyGroups = useCallback(async () => {
    setLoadingMyGroups(true);
    try {
      const res = await fetch(`${API_URL}/api/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setMyGroups(Array.isArray(data) ? data : data.groups || []);
    } catch (err) {
      console.error('Groups fetch error:', err);
    }
    setLoadingMyGroups(false);
  }, [token]);

  const fetchDiscover = useCallback(async () => {
    setLoadingDiscover(true);
    try {
      const q = discoverSearchRef.current.trim();
      const params = new URLSearchParams();
      if (discoverCategory) params.set('category', discoverCategory);
      if (q) params.set('q', q);

      const fetches = [
        fetch(`${API_URL}/api/groups/discover?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        q ? fetch(`${API_URL}/api/organizations/search?q=${encodeURIComponent(q)}`, { headers: { Authorization: `Bearer ${token}` } }) : Promise.resolve(null),
        q ? fetch(`${API_URL}/api/social/users/search?q=${encodeURIComponent(q)}`, { headers: { Authorization: `Bearer ${token}` } }) : Promise.resolve(null),
      ];

      const [groupRes, orgRes, userRes] = await Promise.all(fetches);
      const groupData = await groupRes.json();
      setDiscoverGroups(Array.isArray(groupData) ? groupData : groupData.groups || []);

      if (orgRes) {
        const orgData = await orgRes.json();
        setDiscoverOrgs(Array.isArray(orgData) ? orgData : orgData.organizations || []);
      } else { setDiscoverOrgs([]); }

      if (userRes) {
        const userData = await userRes.json();
        setDiscoverUsers(userData.users || []);
      } else { setDiscoverUsers([]); }
    } catch (err) {
      console.error('Discover fetch error:', err);
    }
    setLoadingDiscover(false);
  }, [token, discoverCategory]);

  const fetchMyOrgs = useCallback(async () => {
    setLoadingOrgs(true);
    try {
      const res = await fetch(`${API_URL}/api/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setMyOrgs(Array.isArray(data) ? data : data.organizations || []);
    } catch (err) {
      console.error('Orgs fetch error:', err);
    }
    setLoadingOrgs(false);
  }, [token]);

  /* ─── Teams ─── */
  const fetchMyTeams = useCallback(async () => {
    setLoadingTeams(true);
    try {
      const res = await fetch(`${API_URL}/api/teams`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setMyTeams(data.teams || []);
    } catch { /* ignore */ }
    setLoadingTeams(false);
  }, [token]);

  const openTeamDetail = async (teamId) => {
    setSelectedTeam(teamId);
    setLoadingTeamDetail(true);
    try {
      const res = await fetch(`${API_URL}/api/teams/${teamId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setTeamDetail(data.team);
    } catch { /* ignore */ }
    setLoadingTeamDetail(false);
  };

  const handleCtAddRow = () => {
    setCtMembers(prev => [...prev, { id: Date.now(), role: '', userId: null, firstName: '', lastName: '', search: '', results: [], loading: false }]);
  };

  const handleCtRoleChange = (idx, role) => {
    setCtMembers(prev => prev.map((m, i) => i === idx ? { ...m, role } : m));
  };

  const handleCtSearch = (idx, q) => {
    setCtMembers(prev => prev.map((m, i) => i === idx ? { ...m, search: q, userId: null, firstName: '', lastName: '' } : m));
    if (ctSearchTimers.current[idx]) clearTimeout(ctSearchTimers.current[idx]);
    if (!q.trim()) {
      setCtMembers(prev => prev.map((m, i) => i === idx ? { ...m, results: [] } : m));
      return;
    }
    ctSearchTimers.current[idx] = setTimeout(async () => {
      setCtMembers(prev => prev.map((m, i) => i === idx ? { ...m, loading: true } : m));
      try {
        const res = await fetch(`${API_URL}/api/social/users/search?q=${encodeURIComponent(q.trim())}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setCtMembers(prev => prev.map((m, i) => i === idx ? { ...m, results: data.users || [], loading: false } : m));
      } catch {
        setCtMembers(prev => prev.map((m, i) => i === idx ? { ...m, results: [], loading: false } : m));
      }
    }, 300);
  };

  const handleCtSelectUser = (idx, u) => {
    setCtMembers(prev => prev.map((m, i) => i === idx ? { ...m, userId: u.id, firstName: u.firstName, lastName: u.lastName, search: `${u.firstName} ${u.lastName}`, results: [] } : m));
  };

  const handleCtRemoveRow = (idx) => {
    setCtMembers(prev => prev.filter((_, i) => i !== idx));
  };

  const handleCreateTeam = async () => {
    if (!ctName.trim() || creatingTeam) return;
    const validMembers = ctMembers.filter(m => m.role && m.userId);
    setCreatingTeam(true);
    try {
      const res = await fetch(`${API_URL}/api/teams`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: ctName, description: ctDesc, ownerRole: ctOwnerRole || undefined })
      });
      if (res.ok) {
        const data = await res.json();
        const teamId = data.team.id;
        // Üyeleri ekle ve davet gönder
        for (const m of validMembers) {
          await fetch(`${API_URL}/api/teams/${teamId}/members`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: m.userId, role: m.role })
          });
        }
        setShowCreateTeam(false);
        setCtName(''); setCtDesc(''); setCtOwnerRole(''); setCtMembers([]);
        fetchMyTeams();
      }
    } catch { /* ignore */ }
    setCreatingTeam(false);
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Bu ekibi silmek istediginize emin misiniz?')) return;
    try {
      await fetch(`${API_URL}/api/teams/${teamId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTeam(null); setTeamDetail(null);
      fetchMyTeams();
    } catch { /* ignore */ }
  };

  const handleRespondInvite = async (teamId, memberId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/teams/${teamId}/members/${memberId}/respond`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      });
      if (res.ok) {
        openTeamDetail(teamId);
        fetchMyTeams();
      }
    } catch { /* ignore */ }
  };

  const handleAddMemberSearch = (q) => {
    setAddMemberSearch(q);
    if (addMemberTimer.current) clearTimeout(addMemberTimer.current);
    if (!q.trim()) { setAddMemberResults([]); return; }
    addMemberTimer.current = setTimeout(async () => {
      setAddMemberLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/social/users/search?q=${encodeURIComponent(q.trim())}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setAddMemberResults(data.users || []);
      } catch { setAddMemberResults([]); }
      setAddMemberLoading(false);
    }, 300);
  };

  const handleAddTeamMember = async (userId) => {
    if (!addMemberRole || !selectedTeam) return;
    try {
      const res = await fetch(`${API_URL}/api/teams/${selectedTeam}/members`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, role: addMemberRole })
      });
      if (res.ok) {
        setAddMemberSearch(''); setAddMemberResults([]);
        openTeamDetail(selectedTeam);
      } else {
        const data = await res.json();
        alert(data.message || 'Uye eklenemedi.');
      }
    } catch { /* ignore */ }
  };

  const handleRemoveTeamMember = async (memberId) => {
    if (!selectedTeam) return;
    try {
      await fetch(`${API_URL}/api/teams/${selectedTeam}/members/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      openTeamDetail(selectedTeam);
    } catch { /* ignore */ }
  };

  /* ─── Load data on tab switch ─── */
  useEffect(() => {
    if (!user) return;
    if (activeTab === 'teams') fetchMyTeams();
    else if (activeTab === 'my') fetchMyGroups();
    else if (activeTab === 'discover') fetchDiscover();
    else if (activeTab === 'orgs') fetchMyOrgs();
  }, [activeTab, user, fetchMyTeams, fetchMyGroups, fetchDiscover, fetchMyOrgs]);

  /* ─── Refetch discover when filters change ─── */
  useEffect(() => {
    if (activeTab === 'discover' && user) fetchDiscover();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoverCategory]);

  /* ─── Auto-search on typing (debounced) ─── */
  useEffect(() => {
    discoverSearchRef.current = discoverSearch;
    if (activeTab !== 'discover' || !user) return;
    const timer = setTimeout(() => fetchDiscover(), 350);
    return () => clearTimeout(timer);
  }, [discoverSearch, activeTab, user, fetchDiscover]);


  /* ═══════════════════════════════════════════════
     GROUP DETAIL
     ═══════════════════════════════════════════════ */

  const openGroupDetail = async (groupId) => {
    setSelectedGroup(groupId);
    setDetailTab('home');
    setLoadingDetail(true);
    setGroupDetail(null);
    setGroupMembers([]);
    setGroupFeed([]);
    try {
      const [detailRes, membersRes] = await Promise.all([
        fetch(`${API_URL}/api/groups/${groupId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/groups/${groupId}/members`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const detail = await detailRes.json();
      const members = await membersRes.json();
      setGroupDetail(detail.group || detail);
      setGroupMembers(Array.isArray(members) ? members : members.members || []);

      // fetch feed if group has it
      const g = detail.group || detail;
      if (g.hasFeed) {
        const feedRes = await fetch(`${API_URL}/api/groups/${groupId}/feed`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const feedData = await feedRes.json();
        setGroupFeed(Array.isArray(feedData) ? feedData : feedData.posts || []);
      }
    } catch (err) {
      console.error('Group detail error:', err);
    }
    setLoadingDetail(false);
  };

  const closeGroupDetail = () => {
    setSelectedGroup(null);
    setGroupDetail(null);
    setGroupMembers([]);
    setGroupFeed([]);
    setNewGroupPost('');
  };


  /* ═══════════════════════════════════════════════
     ORG DETAIL
     ═══════════════════════════════════════════════ */

  const openOrgDetail = async (orgId) => {
    setSelectedOrg(orgId);
    setOrgDetailTab('home');
    setLoadingOrgDetail(true);
    setOrgDetail(null);
    setOrgFeed([]);
    try {
      const [orgRes, feedRes] = await Promise.all([
        fetch(`${API_URL}/api/organizations/${orgId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/organizations/${orgId}/feed`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
      ]);
      const data = await orgRes.json();
      const org = data.organization || data;
      org.members = data.members || [];
      org.myRole = data.myRole || null;
      setOrgDetail(org);
      if (feedRes?.ok) {
        const feedData = await feedRes.json();
        setOrgFeed(feedData.posts || []);
      }
    } catch (err) {
      console.error('Org detail error:', err);
    }
    setLoadingOrgDetail(false);
  };

  const handlePostToOrg = async (orgId) => {
    if (!newOrgPost.trim()) return;
    setPostingToOrg(true);
    try {
      const res = await fetch(`${API_URL}/api/organizations/${orgId}/posts`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newOrgPost.trim() })
      });
      if (res.ok) {
        setNewOrgPost('');
        const feedRes = await fetch(`${API_URL}/api/organizations/${orgId}/feed`, { headers: { Authorization: `Bearer ${token}` } });
        if (feedRes.ok) { const d = await feedRes.json(); setOrgFeed(d.posts || []); }
      }
    } catch (err) { console.error('Org post error:', err); }
    setPostingToOrg(false);
  };

  const closeOrgDetail = () => {
    setSelectedOrg(null);
    setOrgDetail(null);
  };


  /* ═══════════════════════════════════════════════
     ACTIONS
     ═══════════════════════════════════════════════ */

  const handleJoinGroup = async (groupId) => {
    setJoiningId(groupId);
    try {
      await fetch(`${API_URL}/api/groups/${groupId}/join`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      // refresh both tabs so the joined group appears in "my groups"
      fetchDiscover();
      fetchMyGroups();
    } catch (err) {
      console.error('Join error:', err);
    }
    setJoiningId(null);
  };

  const handleLeaveGroup = async (groupId) => {
    try {
      await fetch(`${API_URL}/api/groups/${groupId}/leave`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      closeGroupDetail();
      fetchMyGroups();
    } catch (err) {
      console.error('Leave error:', err);
    }
  };

  const handlePostToGroup = async (groupId) => {
    if (!newGroupPost.trim()) return;
    setPostingToGroup(true);
    try {
      const res = await fetch(`${API_URL}/api/groups/${groupId}/posts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: newGroupPost.trim() }),
      });
      if (res.ok) {
        setNewGroupPost('');
        // refresh group feed
        const feedRes = await fetch(`${API_URL}/api/groups/${groupId}/feed`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const feedData = await feedRes.json();
        setGroupFeed(Array.isArray(feedData) ? feedData : feedData.posts || []);
      }
    } catch (err) {
      console.error('Post to group error:', err);
    }
    setPostingToGroup(false);
  };

  const handleCreateGroup = async () => {
    if (!cgName.trim()) return;
    setCreatingGroup(true);
    try {
      const body = {
        name: cgName.trim(),
        description: cgDesc.trim(),
        category: cgCategory,
        visibility: cgVisibility,
        city: cgCity.trim(),
        tags: cgTags.split(',').map(t => t.trim()).filter(Boolean),
        hasFeed: cgHasFeed,
      };
      const res = await fetch(`${API_URL}/api/groups`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setShowCreateGroup(false);
        setCgName(''); setCgDesc(''); setCgCategory('custom'); setCgVisibility('open');
        setCgCity(''); setCgTags(''); setCgHasFeed(true);
        fetchMyGroups();
      }
    } catch (err) {
      console.error('Create group error:', err);
    }
    setCreatingGroup(false);
  };

  const handleCreateOrg = async () => {
    if (!coName.trim()) return;
    setCreatingOrg(true);
    try {
      const body = {
        name: coName.trim(),
        type: coType,
        description: coDesc.trim(),
        city: coCity.trim(),
        district: coDistrict.trim(),
        website: coWebsite.trim(),
        contactInfo: {
          email: coEmail.trim(),
          phone: coPhone.trim(),
          whatsapp: coWhatsapp.trim(),
          address: coAddress.trim(),
        },
      };
      const res = await fetch(`${API_URL}/api/organizations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setShowCreateOrg(false);
        setCoName(''); setCoType('engineering_bureau'); setCoDesc('');
        setCoCity(''); setCoDistrict(''); setCoWebsite('');
        setCoEmail(''); setCoPhone(''); setCoWhatsapp(''); setCoAddress('');
        fetchMyOrgs();
      }
    } catch (err) {
      console.error('Create org error:', err);
    }
    setCreatingOrg(false);
  };

  /* ─── Discover search on Enter ─── */
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') fetchDiscover();
  };


  /* ═══════════════════════════════════════════════
     LOGOUT HELPER
     ═══════════════════════════════════════════════ */
  const handleLogout = () => {
    localStorage.removeItem('projabze_token');
    localStorage.removeItem('projabze_user');
    navigate('/');
  };

  if (!user) return null;


  /* ═══════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════ */
  return (
    <div className={`groups-page ${theme}`}>
      <Topbar user={user} onLogout={handleLogout} />

      {/* ─── SIDEBAR ─── */}
      <Sidebar activeId="groups" user={user} onLogout={handleLogout} />

      {/* ─── MAIN CONTENT ─── */}
      <main className="groups-main">

        {/* ── Group detail view ── */}
        {selectedGroup ? (
          <div className="groups-detail">
            <button className="groups-back-btn" onClick={closeGroupDetail}>
              <IconBack /> Gruplara Don
            </button>

            {loadingDetail ? (
              <div className="groups-loading">Yukleniyor...</div>
            ) : groupDetail ? (
              <>
                {/* Detail header */}
                <div className="groups-detail-header">
                  <div className="groups-detail-info">
                    <h2>{groupDetail.name}</h2>
                    <div className="groups-detail-meta">
                      <span className={`groups-cat-badge cat-${groupDetail.category}`}>
                        {categoryLabel(groupDetail.category)}
                      </span>
                      <span className="groups-vis-badge">
                        {visibilityIcon(groupDetail.visibility)}
                        {visibilityLabel(groupDetail.visibility)}
                      </span>
                      <span className="groups-member-count">
                        <IconUsers /> {groupDetail.memberCount ?? groupMembers.length} uye
                      </span>
                      {groupDetail.city && (
                        <span className="groups-city">{groupDetail.city}</span>
                      )}
                    </div>
                    {groupDetail.description && (
                      <p className="groups-detail-desc">{groupDetail.description}</p>
                    )}
                  </div>
                </div>

                {/* Detail tabs */}
                <div className="groups-detail-tabs">
                  <button className={`groups-detail-tab ${detailTab === 'home' ? 'active' : ''}`} onClick={() => setDetailTab('home')}>Ana Sayfa</button>
                  <button className={`groups-detail-tab ${detailTab === 'members' ? 'active' : ''}`} onClick={() => setDetailTab('members')}>Uyeler ({groupMembers.length})</button>
                  <button className={`groups-detail-tab ${detailTab === 'settings' ? 'active' : ''}`} onClick={() => setDetailTab('settings')}>Ayarlar</button>
                </div>

                {/* Tab: Home - feed */}
                {detailTab === 'home' && (
                  <div className="groups-section">
                    <Suspense fallback={null}>
                      <PostCreator user={user} token={token} apiUrl={API_URL} targetType="group" targetId={selectedGroup}
                        onPostCreated={(post) => setGroupFeed(prev => [post, ...prev])} />
                    </Suspense>
                    {/* Posts */}
                    <div className="groups-feed-posts">
                      {groupFeed.map((post, i) => {
                        const author = post.author || post.user || {};
                        const initials = (author.firstName?.[0] || '') + (author.lastName?.[0] || '');
                        return (
                          <div key={post.id || i} className="groups-feed-post">
                            <div className="groups-feed-post-header">
                              <div className="groups-member-avatar small">
                                {author.avatar ? <img src={API_URL + author.avatar} alt="" /> : initials}
                              </div>
                              <div>
                                <span className="groups-feed-post-author">{author.firstName} {author.lastName}</span>
                                <span className="groups-feed-post-time">{relativeTime(post.createdAt)}</span>
                              </div>
                            </div>
                            <p className="groups-feed-post-content">{post.content}</p>
                          </div>
                        );
                      })}
                      {groupFeed.length === 0 && (
                        <div className="groups-empty-text">Henuz gonderi yok. Ilk gonderiyi siz atin!</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Tab: Members */}
                {detailTab === 'members' && (
                  <div className="groups-section">
                    <div className="groups-member-list">
                      {groupMembers.map((m, i) => {
                        const mu = m.user || m;
                        const ini = (mu.firstName?.[0] || '') + (mu.lastName?.[0] || '');
                        return (
                          <div key={mu.id || i} className="groups-member-item" onClick={() => navigate(`/profile/${mu.id}`)}>
                            <div className="groups-member-avatar">
                              {mu.avatar ? <img src={API_URL + mu.avatar} alt="" /> : ini}
                            </div>
                            <div className="groups-member-info">
                              <span className="groups-member-name">{mu.firstName} {mu.lastName}</span>
                              {m.role && <span className="groups-member-role">{m.role}</span>}
                            </div>
                          </div>
                        );
                      })}
                      {groupMembers.length === 0 && (
                        <div className="groups-empty-text">Henuz uye yok.</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Tab: Settings */}
                {detailTab === 'settings' && (
                  <div className="groups-section groups-settings">
                    <div className="groups-settings-row">
                      <span className="groups-settings-label">Grup Adi</span>
                      <span className="groups-settings-value">{groupDetail.name}</span>
                    </div>
                    <div className="groups-settings-row">
                      <span className="groups-settings-label">Kategori</span>
                      <span className="groups-settings-value">{categoryLabel(groupDetail.category)}</span>
                    </div>
                    <div className="groups-settings-row">
                      <span className="groups-settings-label">Gorunurluk</span>
                      <span className="groups-settings-value">{visibilityLabel(groupDetail.visibility)}</span>
                    </div>
                    <div className="groups-settings-row">
                      <span className="groups-settings-label">Sehir</span>
                      <span className="groups-settings-value">{groupDetail.city || '-'}</span>
                    </div>
                    <div className="groups-settings-row">
                      <span className="groups-settings-label">Etiketler</span>
                      <span className="groups-settings-value">{groupDetail.tags?.join(', ') || '-'}</span>
                    </div>
                    <div className="groups-settings-row">
                      <span className="groups-settings-label">Olusturulma</span>
                      <span className="groups-settings-value">{groupDetail.createdAt ? new Date(groupDetail.createdAt).toLocaleDateString('tr-TR') : '-'}</span>
                    </div>
                    <div style={{marginTop:'1rem'}}>
                      <button className="groups-leave-btn" onClick={() => handleLeaveGroup(selectedGroup)}>
                        <IconLogout /> Gruptan Ayril
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="groups-empty-text">Grup bulunamadi.</div>
            )}
          </div>
        ) : selectedOrg ? (
          /* ── Org detail view ── */
          <div className="groups-detail">
            <button className="groups-back-btn" onClick={closeOrgDetail}>
              <IconBack /> Organizasyonlara Don
            </button>

            {loadingOrgDetail ? (
              <div className="groups-loading">Yukleniyor...</div>
            ) : orgDetail ? (
              <>
                <div className="groups-detail-header">
                  <div className="groups-detail-info">
                    <h2>{orgDetail.name}</h2>
                    <div className="groups-detail-meta">
                      <span className="groups-org-type-badge">
                        <IconBuilding /> {orgTypeLabel(orgDetail.type)}
                      </span>
                      <span className="groups-member-count">
                        <IconUsers /> {orgDetail.memberCount ?? orgDetail.members?.length ?? 0} uye
                      </span>
                      {orgDetail.city && (
                        <span className="groups-city">{orgDetail.city}</span>
                      )}
                    </div>
                    {orgDetail.description && (
                      <p className="groups-detail-desc">{orgDetail.description}</p>
                    )}
                    {orgDetail.website && (
                      <a className="groups-org-website" href={orgDetail.website} target="_blank" rel="noopener noreferrer">
                        {orgDetail.website}
                      </a>
                    )}
                  </div>
                </div>

                {/* Org detail tabs */}
                <div className="groups-detail-tabs">
                  <button className={`groups-detail-tab ${orgDetailTab === 'home' ? 'active' : ''}`} onClick={() => setOrgDetailTab('home')}>Ana Sayfa</button>
                  <button className={`groups-detail-tab ${orgDetailTab === 'members' ? 'active' : ''}`} onClick={() => setOrgDetailTab('members')}>Uyeler ({orgDetail.members?.length || 0})</button>
                  <button className={`groups-detail-tab ${orgDetailTab === 'inbox' ? 'active' : ''}`} onClick={() => setOrgDetailTab('inbox')}>Gelen Kutusu</button>
                  {(orgDetail.myRole === 'owner' || orgDetail.myRole === 'admin') && (
                    <button className={`groups-detail-tab ${orgDetailTab === 'settings' ? 'active' : ''}`} onClick={() => setOrgDetailTab('settings')}>Ayarlar</button>
                  )}
                </div>

                {/* Org Tab: Home */}
                {orgDetailTab === 'home' && (
                  <div className="groups-section">
                    <Suspense fallback={null}>
                      <PostCreator user={user} token={token} apiUrl={API_URL} targetType="organization" targetId={selectedOrg}
                        onPostCreated={(post) => setOrgFeed(prev => [post, ...prev])} />
                    </Suspense>
                    <div className="groups-feed-posts">
                      {orgFeed.map((post, i) => {
                        const author = post.author || {};
                        const ini = (author.firstName?.[0] || '') + (author.lastName?.[0] || '');
                        return (
                          <div key={post.id || i} className="groups-feed-post">
                            <div className="groups-feed-post-header">
                              <div className="groups-member-avatar small">
                                {author.avatar ? <img src={API_URL + author.avatar} alt="" /> : ini}
                              </div>
                              <div>
                                <span className="groups-feed-post-author">{author.firstName} {author.lastName}</span>
                                <span className="groups-feed-post-time">{relativeTime(post.createdAt)}</span>
                              </div>
                            </div>
                            <p className="groups-feed-post-content">{post.content}</p>
                          </div>
                        );
                      })}
                      {orgFeed.length === 0 && (
                        <div className="groups-empty-text">Henuz gonderi yok. Ilk gonderiyi siz atin!</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Org Tab: Members */}
                {orgDetailTab === 'members' && orgDetail.members?.length > 0 && (
                  <div className="groups-section">
                    <div className="groups-member-list">
                      {orgDetail.members.map((m, i) => {
                        const mu = m.user || m;
                        const ini = (mu.firstName?.[0] || '') + (mu.lastName?.[0] || '');
                        return (
                          <div key={mu.id || i} className="groups-member-item" onClick={() => navigate(`/profile/${mu.id}`)}>
                            <div className="groups-member-avatar">
                              {mu.avatar ? <img src={API_URL + mu.avatar} alt="" /> : ini}
                            </div>
                            <div className="groups-member-info">
                              <span className="groups-member-name">{mu.firstName} {mu.lastName}</span>
                              {m.role && <span className="groups-member-role">{m.role}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Org Tab: Inbox */}
                {orgDetailTab === 'inbox' && (
                  <OrgInbox orgId={selectedOrg} token={token} />
                )}

                {/* Org Tab: Settings */}
                {orgDetailTab === 'settings' && (orgDetail.myRole === 'owner' || orgDetail.myRole === 'admin') && (
                  <OrgSettingsTab orgDetail={orgDetail} token={token} onSaved={(updated) => setOrgDetail(prev => ({ ...prev, ...updated }))} />
                )}
              </>
            ) : (
              <div className="groups-empty-text">Organizasyon bulunamadi.</div>
            )}
          </div>
        ) : (
          /* ── Tab content ── */
          <>
            {/* Tabs */}
            <div className="groups-tabs">
              <button className={`groups-tab ${activeTab === 'teams' ? 'active' : ''}`} onClick={() => { setSelectedTeam(null); setTeamDetail(null); setActiveTab('teams'); }}>
                <IconUsers /> Ekiplerim
              </button>
              <button className={`groups-tab ${activeTab === 'my' ? 'active' : ''}`} onClick={() => setActiveTab('my')}>
                <IconUsers /> Gruplarim
              </button>
              <button className={`groups-tab ${activeTab === 'discover' ? 'active' : ''}`} onClick={() => setActiveTab('discover')}>
                <IconSearch /> Kesfet
              </button>
              <button className={`groups-tab ${activeTab === 'orgs' ? 'active' : ''}`} onClick={() => setActiveTab('orgs')}>
                <IconBuilding /> Organizasyonlarim
              </button>
            </div>

            {/* ─── TAB: Ekiplerim ─── */}
            {activeTab === 'teams' && !selectedTeam && (
              <div className="groups-tab-content">
                <div className="groups-tab-header">
                  <div>
                    <h1>Ekiplerim</h1>
                    <p>Proje paydas ekipleri</p>
                  </div>
                  <button className="groups-create-btn" onClick={() => setShowCreateTeam(true)}>
                    <IconPlus /> Ekip Olustur
                  </button>
                </div>
                {loadingTeams ? (
                  <div className="groups-loading">Yukleniyor...</div>
                ) : myTeams.length > 0 ? (
                  <div className="groups-grid">
                    {myTeams.map(t => (
                      <div key={t.id} className="groups-card" onClick={() => openTeamDetail(t.id)}>
                        <div className="groups-card-top">
                          <h3 className="groups-card-name">{t.name}</h3>
                          {t.isOwner && <span className="groups-cat-badge">Sahip</span>}
                        </div>
                        {t.description && <p className="groups-card-desc">{t.description}</p>}
                        <div className="groups-card-footer">
                          <span className="groups-card-members"><IconUsers /> {t.memberCount} uye</span>
                          {t.pendingCount > 0 && <span className="groups-card-pending">{t.pendingCount} bekliyor</span>}
                          {t.myStatus === 'pending' && <span className="groups-card-invite-badge">Davet</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="groups-empty">
                    <h3>Henuz bir ekibiniz yok</h3>
                    <p>Proje paydas ekipleri olusturarak uyelerinizi yonetin.</p>
                    <button className="groups-create-btn" onClick={() => setShowCreateTeam(true)}>
                      <IconPlus /> Ekip Olustur
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ─── TAB: Ekip Detay ─── */}
            {activeTab === 'teams' && selectedTeam && (
              <div className="groups-tab-content">
                <button className="groups-back-btn" onClick={() => { setSelectedTeam(null); setTeamDetail(null); }}>
                  ← Ekiplerim
                </button>
                {loadingTeamDetail ? (
                  <div className="groups-loading">Yukleniyor...</div>
                ) : teamDetail && (
                  <div className="groups-detail">
                    <div className="groups-detail-header">
                      <div>
                        <h1>{teamDetail.name}</h1>
                        {teamDetail.description && <p>{teamDetail.description}</p>}
                      </div>
                      {teamDetail.isOwner && (
                        <button className="groups-delete-btn" onClick={() => handleDeleteTeam(teamDetail.id)}>Ekibi Sil</button>
                      )}
                    </div>

                    {/* Davet Banneri — davet edilen kullanıcı için */}
                    {!teamDetail.isOwner && teamDetail.myStatus === 'pending' && (
                      <div className="teams-invite-banner">
                        <span>Bu ekibe davet edildiniz.</span>
                        <div className="teams-invite-actions">
                          <button className="teams-invite-accept" onClick={() => handleRespondInvite(teamDetail.id, teamDetail.myMemberId, 'accept')}>Kabul Et</button>
                          <button className="teams-invite-reject" onClick={() => handleRespondInvite(teamDetail.id, teamDetail.myMemberId, 'reject')}>Reddet</button>
                        </div>
                      </div>
                    )}
                    {!teamDetail.isOwner && teamDetail.myStatus === 'accepted' && (
                      <div className="teams-invite-banner teams-invite-accepted">
                        <span>Bu ekibin uyesisiniz.</span>
                      </div>
                    )}
                    {!teamDetail.isOwner && teamDetail.myStatus === 'rejected' && (
                      <div className="teams-invite-banner teams-invite-rejected">
                        <span>Bu daveti reddetiniz.</span>
                      </div>
                    )}

                    {/* Üye Ekleme — sadece sahip */}
                    {teamDetail.isOwner && (
                      <div className="teams-add-member">
                        <h3>Uye Davet Et</h3>
                        <div className="teams-add-row">
                          <select className="teams-role-select" value={addMemberRole} onChange={e => setAddMemberRole(e.target.value)}>
                            <option value="">Rol secin...</option>
                            {TEAM_ROLES.map(r => (
                              <option key={r.key} value={r.key}>{r.label}</option>
                            ))}
                          </select>
                          <input
                            className="teams-search-input"
                            type="text"
                            placeholder={addMemberRole ? 'Kisi ara...' : 'Once rol secin'}
                            disabled={!addMemberRole}
                            value={addMemberSearch}
                            onChange={e => handleAddMemberSearch(e.target.value)}
                          />
                        </div>
                        {addMemberLoading && <div className="teams-search-hint">Araniyor...</div>}
                        {addMemberResults.length > 0 && (
                          <div className="teams-search-results">
                            {addMemberResults.map(u => (
                              <div key={u.id} className="teams-search-result" onClick={() => handleAddTeamMember(u.id)}>
                                <div className="teams-result-avatar">{u.avatar ? <img src={API_URL + u.avatar} alt="" /> : `${u.firstName?.[0] || ''}${u.lastName?.[0] || ''}`}</div>
                                <span className="teams-result-name">{u.firstName} {u.lastName}</span>
                                <button className="teams-result-add">Davet Et</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Üye Listesi — sahip için duruma göre gruplu */}
                    {teamDetail.isOwner ? (
                      <div className="teams-members">
                        {/* Ekip Üyeleri (kabul edenler — kurucu dahil) */}
                        {(() => {
                          const accepted = (teamDetail.members || []).filter(m => m.status === 'accepted');
                          return accepted.length > 0 && (
                            <div className="teams-status-section">
                              <h3 className="teams-status-title teams-status-accepted">Ekip Uyeleri ({accepted.length})</h3>
                              <div className="teams-member-list">
                                {TEAM_ROLES.map(role => {
                                  const rm = accepted.filter(m => m.role === role.key);
                                  if (!rm.length) return null;
                                  return (
                                    <div key={role.key} className="teams-role-group">
                                      <div className="teams-role-label">{role.label}</div>
                                      {rm.map(m => (
                                        <div key={m.id} className="teams-member-row">
                                          <div className="teams-member-avatar">{m.avatar ? <img src={API_URL + m.avatar} alt="" /> : `${m.firstName?.[0] || ''}${m.lastName?.[0] || ''}`}</div>
                                          <span className="teams-member-name">{m.firstName} {m.lastName}</span>
                                          {m.userId === teamDetail.createdBy && <span className="teams-owner-badge">Kurucu</span>}
                                          {m.userId !== teamDetail.createdBy && <button className="teams-member-remove" onClick={() => handleRemoveTeamMember(m.id)}>×</button>}
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Bekleyenler (davet gönderildi) */}
                        {(() => {
                          const pending = (teamDetail.members || []).filter(m => m.status === 'pending');
                          return pending.length > 0 && (
                            <div className="teams-status-section">
                              <h3 className="teams-status-title teams-status-pending">Davet Gonderilenler ({pending.length})</h3>
                              <div className="teams-member-list">
                                {TEAM_ROLES.map(role => {
                                  const rm = pending.filter(m => m.role === role.key);
                                  if (!rm.length) return null;
                                  return (
                                    <div key={role.key} className="teams-role-group">
                                      <div className="teams-role-label">{role.label}</div>
                                      {rm.map(m => (
                                        <div key={m.id} className="teams-member-row">
                                          <div className="teams-member-avatar">{m.avatar ? <img src={API_URL + m.avatar} alt="" /> : `${m.firstName?.[0] || ''}${m.lastName?.[0] || ''}`}</div>
                                          <span className="teams-member-name">{m.firstName} {m.lastName}</span>
                                          <span className="teams-status-badge pending">Bekliyor</span>
                                          <button className="teams-member-remove" onClick={() => handleRemoveTeamMember(m.id)}>×</button>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Reddedenler */}
                        {(() => {
                          const rejected = (teamDetail.members || []).filter(m => m.status === 'rejected');
                          return rejected.length > 0 && (
                            <div className="teams-status-section">
                              <h3 className="teams-status-title teams-status-rejected">Reddeden ({rejected.length})</h3>
                              <div className="teams-member-list">
                                {rejected.map(m => (
                                  <div key={m.id} className="teams-member-row">
                                    <div className="teams-member-avatar">{m.avatar ? <img src={API_URL + m.avatar} alt="" /> : `${m.firstName?.[0] || ''}${m.lastName?.[0] || ''}`}</div>
                                    <span className="teams-member-name">{m.firstName} {m.lastName}</span>
                                    <span className="teams-role-hint">{teamRoleLabel(m.role)}</span>
                                    <span className="teams-status-badge rejected">Reddetti</span>
                                    <button className="teams-member-remove" onClick={() => handleRemoveTeamMember(m.id)}>×</button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                        {(teamDetail.members || []).length === 0 && (
                          <p className="teams-empty-hint">Henuz uye davet edilmemis.</p>
                        )}
                      </div>
                    ) : (
                      /* Üye görünümü — kabul edenleri göster (kurucu dahil) */
                      <div className="teams-members">
                        <h3>Ekip Uyeleri</h3>
                        {(() => {
                          const accepted = (teamDetail.members || []).filter(m => m.status === 'accepted');
                          return accepted.length > 0 ? (
                            <div className="teams-member-list">
                              {TEAM_ROLES.map(role => {
                                const rm = accepted.filter(m => m.role === role.key);
                                if (!rm.length) return null;
                                return (
                                  <div key={role.key} className="teams-role-group">
                                    <div className="teams-role-label">{role.label}</div>
                                    {rm.map(m => (
                                      <div key={m.id} className="teams-member-row">
                                        <div className="teams-member-avatar">{m.avatar ? <img src={API_URL + m.avatar} alt="" /> : `${m.firstName?.[0] || ''}${m.lastName?.[0] || ''}`}</div>
                                        <span className="teams-member-name">{m.firstName} {m.lastName}</span>
                                        {m.userId === teamDetail.createdBy && <span className="teams-owner-badge">Kurucu</span>}
                                      </div>
                                    ))}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="teams-empty-hint">Henuz kabul edilen uye yok.</p>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ─── TAB: Gruplarim ─── */}
            {activeTab === 'my' && (
              <div className="groups-tab-content">
                <div className="groups-tab-header">
                  <div>
                    <h1>Gruplarim</h1>
                    <p>Uye oldugunuz topluluklar</p>
                  </div>
                  <button className="groups-create-btn" onClick={() => setShowCreateGroup(true)}>
                    <IconPlus /> Grup Olustur
                  </button>
                </div>

                {loadingMyGroups ? (
                  <div className="groups-loading">Yukleniyor...</div>
                ) : myGroups.length > 0 ? (
                  <div className="groups-grid">
                    {myGroups.map(g => (
                      <div key={g.id || g._id} className="groups-card" onClick={() => openGroupDetail(g.id || g._id)}>
                        <div className="groups-card-top">
                          <h3 className="groups-card-name">{g.name}</h3>
                          <span className="groups-card-vis" title={visibilityLabel(g.visibility)}>
                            {visibilityIcon(g.visibility)}
                          </span>
                        </div>
                        <div className="groups-card-badges">
                          <span className={`groups-cat-badge cat-${g.category}`}>
                            {categoryLabel(g.category)}
                          </span>
                        </div>
                        {g.description && (
                          <p className="groups-card-desc">{g.description}</p>
                        )}
                        <div className="groups-card-footer">
                          <span className="groups-member-count">
                            <IconUsers /> {g.memberCount ?? 0} uye
                          </span>
                          {g.city && <span className="groups-city">{g.city}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="groups-empty">
                    <IconUsers />
                    <h3>Henuz bir gruba uye degilsiniz</h3>
                    <p>Kesfet sekmesinden gruplara katilabilir veya yeni bir grup olusturabilirsiniz.</p>
                    <button className="groups-create-btn" onClick={() => setActiveTab('discover')}>
                      <IconSearch /> Gruplari Kesfet
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ─── TAB: Kesfet ─── */}
            {activeTab === 'discover' && (
              <div className="groups-tab-content">
                <div className="groups-tab-header">
                  <div>
                    <h1>Kesfet</h1>
                    <p>Yeni topluluklar kesfet ve katil</p>
                  </div>
                </div>

                {/* Filters */}
                <div className="groups-discover-filters">
                  <div className="groups-search-wrap">
                    <IconSearch />
                    <input
                      type="text"
                      placeholder="Kisi, organizasyon veya grup ara..."
                      value={discoverSearch}
                      onChange={e => setDiscoverSearch(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                    />
                    {discoverSearch && (
                      <button className="groups-search-clear" onClick={() => { setDiscoverSearch(''); }}>
                        <IconX />
                      </button>
                    )}
                  </div>
                  <div className="groups-filter-cats">
                    <button
                      className={`groups-filter-chip ${discoverCategory === '' ? 'active' : ''}`}
                      onClick={() => setDiscoverCategory('')}
                    >
                      Tumu
                    </button>
                    {CATEGORIES.map(c => (
                      <button
                        key={c.key}
                        className={`groups-filter-chip ${discoverCategory === c.key ? 'active' : ''}`}
                        onClick={() => setDiscoverCategory(c.key)}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>

                {loadingDiscover ? (
                  <div className="groups-loading">Yukleniyor...</div>
                ) : (discoverUsers.length > 0 || discoverOrgs.length > 0 || discoverGroups.length > 0) ? (
                  <>
                    {/* Kisiler */}
                    {discoverUsers.length > 0 && (
                      <>
                        <div className="groups-discover-section-title"><IconUsers /> Kisiler</div>
                        <div className="groups-user-list">
                          {discoverUsers.map(u => {
                            const ini = (u.firstName?.[0] || '') + (u.lastName?.[0] || '');
                            return (
                              <div key={u.id} className="groups-user-card" onClick={() => navigate(`/social/profile/${u.id}`)}>
                                <div className="groups-member-avatar">
                                  {u.avatar ? <img src={API_URL + u.avatar} alt="" /> : ini}
                                </div>
                                <div className="groups-user-card-info">
                                  <span className="groups-user-card-name">{u.firstName} {u.lastName}</span>
                                  <span className="groups-user-card-meta">
                                    {u.sectorRole && <span>{u.sectorRole}</span>}
                                    {u.city && <span>{u.city}</span>}
                                  </span>
                                </div>
                                {u.isFollowing !== undefined && (
                                  <span className={`groups-user-card-badge ${u.isFollowing ? 'following' : ''}`}>
                                    {u.isFollowing ? 'Takip ediliyor' : 'Takip et'}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {/* Organizasyonlar */}
                    {discoverOrgs.length > 0 && (
                      <>
                        <div className="groups-discover-section-title"><IconBuilding /> Organizasyonlar</div>
                        <div className="groups-grid">
                          {discoverOrgs.map(o => (
                            <div key={o.id} className="groups-card org" onClick={() => { openOrgDetail(o.id); setActiveTab('orgs'); }}>
                              <div className="groups-card-top">
                                <h3 className="groups-card-name">{o.name}</h3>
                              </div>
                              <div className="groups-card-badges">
                                <span className="groups-org-type-badge"><IconBuilding /> {orgTypeLabel(o.type)}</span>
                              </div>
                              {o.description && <p className="groups-card-desc">{o.description}</p>}
                              <div className="groups-card-footer">
                                <span className="groups-member-count"><IconUsers /> {o.memberCount ?? 0} uye</span>
                                {o.city && <span className="groups-city">{o.city}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* Gruplar */}
                    {discoverGroups.length > 0 && (
                      <>
                        {(discoverOrgs.length > 0 || discoverUsers.length > 0) && <div className="groups-discover-section-title"><IconGlobe /> Gruplar</div>}
                        <div className="groups-grid">
                          {discoverGroups.map(g => {
                            const isJoining = joiningId === (g.id || g._id);
                            return (
                              <div key={g.id || g._id} className="groups-card discover">
                                <div className="groups-card-top">
                                  <h3 className="groups-card-name">{g.name}</h3>
                                  <span className="groups-card-vis" title={visibilityLabel(g.visibility)}>
                                    {visibilityIcon(g.visibility)}
                                  </span>
                                </div>
                                <div className="groups-card-badges">
                                  <span className={`groups-cat-badge cat-${g.category}`}>
                                    {categoryLabel(g.category)}
                                  </span>
                                </div>
                                {g.description && (
                                  <p className="groups-card-desc">{g.description}</p>
                                )}
                                <div className="groups-card-footer">
                                  <span className="groups-member-count">
                                    <IconUsers /> {g.memberCount ?? 0} uye
                                  </span>
                                  {g.city && <span className="groups-city">{g.city}</span>}
                                </div>
                                <button
                                  className="groups-join-btn"
                                  onClick={(e) => { e.stopPropagation(); handleJoinGroup(g.id || g._id); }}
                                  disabled={isJoining}
                                >
                                  {isJoining ? 'Katiliniyor...' : 'Katil'}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </>
                ) : discoverSearch.trim() ? (
                  <div className="groups-empty">
                    <IconSearch />
                    <h3>Sonuc bulunamadi</h3>
                    <p>Farkli arama terimleri deneyin.</p>
                  </div>
                ) : (
                  <div className="groups-empty">
                    <IconSearch />
                    <h3>Arama yapin</h3>
                    <p>Kisi, organizasyon veya grup aramak icin yukari yazin.</p>
                  </div>
                )}
              </div>
            )}

            {/* ─── TAB: Organizasyonlarim ─── */}
            {activeTab === 'orgs' && (
              <div className="groups-tab-content">
                <div className="groups-tab-header">
                  <div>
                    <h1>Organizasyonlarim</h1>
                    <p>Firmalar, kurumlar ve ekipler</p>
                  </div>
                  <button className="groups-create-btn" onClick={() => setShowCreateOrg(true)}>
                    <IconPlus /> Olustur
                  </button>
                </div>

                {myOrgs.length > 1 && (
                  <div className="groups-discover-filters">
                    <div className="groups-search-wrap">
                      <IconSearch />
                      <input
                        type="text"
                        placeholder="Organizasyonlarimda ara..."
                        value={orgSearch}
                        onChange={e => setOrgSearch(e.target.value)}
                      />
                      {orgSearch && (
                        <button className="groups-search-clear" onClick={() => setOrgSearch('')}>
                          <IconX />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {loadingOrgs ? (
                  <div className="groups-loading">Yukleniyor...</div>
                ) : myOrgs.length > 0 ? (
                  <div className="groups-grid">
                    {myOrgs.filter(o => !orgSearch || o.name.toLowerCase().includes(orgSearch.toLowerCase()) || (o.description || '').toLowerCase().includes(orgSearch.toLowerCase()) || orgTypeLabel(o.type).toLowerCase().includes(orgSearch.toLowerCase())).map(o => (
                      <div key={o.id || o._id} className="groups-card org" onClick={() => openOrgDetail(o.id || o._id)}>
                        <div className="groups-card-top">
                          <h3 className="groups-card-name">{o.name}</h3>
                        </div>
                        <div className="groups-card-badges">
                          <span className="groups-org-type-badge">
                            <IconBuilding /> {orgTypeLabel(o.type)}
                          </span>
                        </div>
                        {o.description && (
                          <p className="groups-card-desc">{o.description}</p>
                        )}
                        <div className="groups-card-footer">
                          <span className="groups-member-count">
                            <IconUsers /> {o.memberCount ?? 0} uye
                          </span>
                          {o.city && <span className="groups-city">{o.city}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="groups-empty">
                    <IconBuilding />
                    <h3>Henuz bir organizasyonunuz yok</h3>
                    <p>Firmanizi veya ekibinizi olusturun.</p>
                    <button className="groups-create-btn" onClick={() => setShowCreateOrg(true)}>
                      <IconPlus /> Organizasyon Olustur
                    </button>
                  </div>
                )}
              </div>
            )}

          </>
        )}
      </main>

      {/* ─── RIGHT SIDEBAR ─── */}
      <RightSidebar />


      {/* ═══════════════════════════════════════════════
         MODALS
         ═══════════════════════════════════════════════ */}

      {/* Create Team Modal */}
      {showCreateTeam && (
        <div className="groups-modal-overlay" onClick={() => setShowCreateTeam(false)}>
          <div className="groups-modal groups-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="groups-modal-header">
              <h2>Yeni Ekip Olustur</h2>
              <button className="groups-modal-close" onClick={() => { setShowCreateTeam(false); setCtMembers([]); }}><IconX /></button>
            </div>
            <div className="groups-modal-body">
              <label>
                Ekip Adi *
                <input type="text" value={ctName} onChange={e => setCtName(e.target.value)} placeholder="Ekip adi girin" />
              </label>
              <label>
                Aciklama
                <textarea value={ctDesc} onChange={e => setCtDesc(e.target.value)} placeholder="Ekip hakkinda kisa aciklama" rows={2} />
              </label>

              <label>
                Ekipteki Rolunuz *
                <select value={ctOwnerRole} onChange={e => setCtOwnerRole(e.target.value)}>
                  <option value="">Rol secin...</option>
                  {TEAM_ROLES.map(r => (
                    <option key={r.key} value={r.key}>{r.label}</option>
                  ))}
                </select>
              </label>

              <div className="ct-members-section">
                <div className="ct-members-header">
                  <span>Ekip Uyeleri</span>
                  <button className="ct-add-row-btn" onClick={handleCtAddRow}><IconPlus /> Uye Ekle</button>
                </div>
                {ctMembers.map((m, idx) => (
                  <div key={m.id} className="ct-member-row">
                    <select className="ct-role-select" value={m.role} onChange={e => handleCtRoleChange(idx, e.target.value)}>
                      <option value="">Rol secin...</option>
                      {TEAM_ROLES.map(r => (
                        <option key={r.key} value={r.key}>{r.label}</option>
                      ))}
                    </select>
                    <div className="ct-search-wrap">
                      <input
                        className="ct-search-input"
                        type="text"
                        placeholder={m.role ? 'Kisi ara...' : 'Once rol secin'}
                        disabled={!m.role}
                        value={m.search}
                        onChange={e => handleCtSearch(idx, e.target.value)}
                      />
                      {m.userId && <span className="ct-selected-check">✓</span>}
                      {m.loading && <span className="ct-search-loading">...</span>}
                      {m.results.length > 0 && !m.userId && (
                        <div className="ct-search-dropdown">
                          {m.results.map(u => (
                            <div key={u.id} className="ct-search-option" onClick={() => handleCtSelectUser(idx, u)}>
                              <span className="ct-option-avatar">{u.avatar ? <img src={API_URL + u.avatar} alt="" /> : `${u.firstName?.[0] || ''}${u.lastName?.[0] || ''}`}</span>
                              {u.firstName} {u.lastName}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <button className="ct-remove-row" onClick={() => handleCtRemoveRow(idx)}>×</button>
                  </div>
                ))}
                {ctMembers.length === 0 && (
                  <div className="ct-empty-hint">Henuz uye eklenmedi. "Uye Ekle" ile baslayın.</div>
                )}
              </div>
            </div>
            <div className="groups-modal-footer">
              <button className="groups-modal-cancel" onClick={() => { setShowCreateTeam(false); setCtMembers([]); }}>Iptal</button>
              <button className="groups-modal-submit" onClick={handleCreateTeam} disabled={creatingTeam || !ctName.trim() || !ctOwnerRole}>
                {creatingTeam ? 'Olusturuluyor...' : `Olustur${ctMembers.filter(m => m.role && m.userId).length > 0 ? ` ve ${ctMembers.filter(m => m.role && m.userId).length} davet gonder` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Group Modal */}
      {showCreateGroup && (
        <div className="groups-modal-overlay" onClick={() => setShowCreateGroup(false)}>
          <div className="groups-modal" onClick={e => e.stopPropagation()}>
            <div className="groups-modal-header">
              <h2>Yeni Grup Olustur</h2>
              <button className="groups-modal-close" onClick={() => setShowCreateGroup(false)}><IconX /></button>
            </div>
            <div className="groups-modal-body">
              <label>
                Grup Adi *
                <input type="text" value={cgName} onChange={e => setCgName(e.target.value)} placeholder="Grup adi girin" />
              </label>
              <label>
                Aciklama
                <textarea value={cgDesc} onChange={e => setCgDesc(e.target.value)} placeholder="Grup hakkinda kisa aciklama" rows={3} />
              </label>
              <div className="groups-modal-row">
                <label>
                  Kategori
                  <select value={cgCategory} onChange={e => setCgCategory(e.target.value)}>
                    {CATEGORIES.map(c => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Gorunurluk
                  <select value={cgVisibility} onChange={e => setCgVisibility(e.target.value)}>
                    {VISIBILITY_OPTIONS.map(v => (
                      <option key={v.key} value={v.key}>{v.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label>
                Sehir
                <AutocompleteInput value={cgCity} onChange={val => setCgCity(val)} options={CITY_LIST} placeholder="Il secin veya yazin..." />
              </label>
              <label>
                Etiketler
                <input type="text" value={cgTags} onChange={e => setCgTags(e.target.value)} placeholder="Virgul ile ayirin: elektrik, tesisat" />
              </label>
              <label className="groups-modal-checkbox">
                <input type="checkbox" checked={cgHasFeed} onChange={e => setCgHasFeed(e.target.checked)} />
                Grup akisi (feed) aktif olsun
              </label>
            </div>
            <div className="groups-modal-footer">
              <button className="groups-modal-cancel" onClick={() => setShowCreateGroup(false)}>Iptal</button>
              <button className="groups-modal-submit" onClick={handleCreateGroup} disabled={creatingGroup || !cgName.trim()}>
                {creatingGroup ? 'Olusturuluyor...' : 'Olustur'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Organization Modal */}
      {showCreateOrg && (
        <div className="groups-modal-overlay" onClick={() => setShowCreateOrg(false)}>
          <div className="groups-modal" onClick={e => e.stopPropagation()}>
            <div className="groups-modal-header">
              <h2>Yeni Organizasyon Olustur</h2>
              <button className="groups-modal-close" onClick={() => setShowCreateOrg(false)}><IconX /></button>
            </div>
            <div className="groups-modal-body">
              <label>
                Organizasyon Adi *
                <input type="text" value={coName} onChange={e => setCoName(e.target.value)} placeholder="Organizasyon adi" />
              </label>
              <label>
                Tur
                <select value={coType} onChange={e => setCoType(e.target.value)}>
                  {ORG_TYPES.map(o => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Aciklama
                <textarea value={coDesc} onChange={e => setCoDesc(e.target.value)} placeholder="Organizasyon hakkinda" rows={3} />
              </label>
              <div className="groups-modal-row">
                <label style={{flex:1}}>
                  Il
                  <AutocompleteInput value={coCity} onChange={val => { setCoCity(val); setCoDistrict(''); }} options={CITY_LIST} placeholder="Il secin..." />
                </label>
                <label style={{flex:1}}>
                  Ilce
                  <AutocompleteInput value={coDistrict} onChange={val => setCoDistrict(val)} options={coCity ? getDistricts(coCity) : []} placeholder="Ilce secin..." />
                </label>
              </div>
              <label>
                Website
                <input type="text" value={coWebsite} onChange={e => setCoWebsite(e.target.value)} placeholder="https://example.com" />
              </label>

              <div className="groups-modal-section-title">Iletisim Bilgileri</div>
              <div className="groups-modal-row">
                <label style={{flex:1}}>
                  E-posta
                  <input type="email" value={coEmail} onChange={e => setCoEmail(e.target.value)} placeholder="info@firma.com" />
                </label>
                <label style={{flex:1}}>
                  Telefon
                  <input type="tel" value={coPhone} onChange={e => setCoPhone(e.target.value)} placeholder="0312 000 00 00" />
                </label>
              </div>
              <div className="groups-modal-row">
                <label style={{flex:1}}>
                  WhatsApp
                  <input type="tel" value={coWhatsapp} onChange={e => setCoWhatsapp(e.target.value)} placeholder="+90 5XX XXX XX XX" />
                </label>
                <label style={{flex:1}}>
                  Adres
                  <input type="text" value={coAddress} onChange={e => setCoAddress(e.target.value)} placeholder="Acik adres" />
                </label>
              </div>
            </div>
            <div className="groups-modal-footer">
              <button className="groups-modal-cancel" onClick={() => setShowCreateOrg(false)}>Iptal</button>
              <button className="groups-modal-submit" onClick={handleCreateOrg} disabled={creatingOrg || !coName.trim()}>
                {creatingOrg ? 'Olusturuluyor...' : 'Olustur'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Groups;
